import React, { useState, useEffect, useMemo } from 'react';
import { adminAPI } from '../../services/api';

// Admin-set HOME-SCREEN priority for restaurants (user Flutter app + customer web).
// Higher number = pinned higher on the home list; 0 = normal (nearest-first).
// Only REORDERS restaurants already within the customer's max distance — it never
// surfaces an out-of-range restaurant. Organized by city (the restaurant's own
// address.city). Backend: PATCH /admin/restaurants/:id/home-priority. No app release.
function HomePriorityManagement() {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [search, setSearch] = useState('');
  const [drafts, setDrafts] = useState({});   // id -> string value being edited
  const [savingId, setSavingId] = useState(null);
  const [savedId, setSavedId] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getAllRestaurants({ limit: 500 });
      const list = res.data?.data?.restaurants || res.data?.restaurants || [];
      setRestaurants(Array.isArray(list) ? list : []);
      setError('');
    } catch (e) {
      setError('Failed to load restaurants');
    } finally {
      setLoading(false);
    }
  };

  const cities = useMemo(() => {
    const s = new Set();
    restaurants.forEach(r => { const c = r.address?.city; if (c) s.add(c); });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [restaurants]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return restaurants
      .filter(r => !cityFilter || r.address?.city === cityFilter)
      .filter(r => !q || (r.name || '').toLowerCase().includes(q))
      .sort((a, b) => (b.homePriority || 0) - (a.homePriority || 0) || (a.name || '').localeCompare(b.name || ''));
  }, [restaurants, cityFilter, search]);

  const draftFor = (r) => (drafts[r._id] !== undefined ? drafts[r._id] : String(r.homePriority || 0));

  const save = async (r) => {
    const val = Math.max(0, Math.round(Number(draftFor(r))) || 0);
    setSavingId(r._id);
    try {
      const res = await adminAPI.updateRestaurantHomePriority(r._id, val);
      const updated = res.data?.data?.restaurant;
      const newPriority = updated?.homePriority ?? val;
      setRestaurants(prev => prev.map(x => x._id === r._id ? { ...x, homePriority: newPriority } : x));
      setDrafts(prev => { const n = { ...prev }; delete n[r._id]; return n; });
      setSavedId(r._id);
      setTimeout(() => setSavedId(cur => (cur === r._id ? null : cur)), 1500);
    } catch (e) {
      setError('Failed to save priority for ' + (r.name || 'restaurant'));
    } finally {
      setSavingId(null);
    }
  };

  const prioritizedCount = restaurants.filter(r => (r.homePriority || 0) > 0).length;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 24 }}>
      <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0F172A', margin: 0 }}>Home Screen Priority</h1>
      <p style={{ color: '#64748B', fontSize: 14, margin: '6px 0 18px' }}>
        Higher priority pins a restaurant higher on the user-app home list (0 = normal, nearest-first).
        Priority only reorders restaurants already within a customer's delivery range — it never shows an out-of-range restaurant.
        <b> {prioritizedCount}</b> restaurant(s) currently prioritized.
      </p>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <select style={{ ...inp, width: 220 }} value={cityFilter} onChange={(e) => setCityFilter(e.target.value)}>
          <option value="">All cities ({restaurants.length})</option>
          {cities.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <input style={{ ...inp, flex: 1, minWidth: 180 }} placeholder="Search restaurant name…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <button onClick={load} style={btn('#64748B', true)}>↻ Refresh</button>
      </div>

      {error ? <p style={{ color: '#EF4444', fontSize: 14 }}>{error}</p> : null}
      {loading ? <p style={{ color: '#64748B' }}>Loading…</p> : (
        <div style={{ border: '1px solid #E2E8F0', borderRadius: 12, overflow: 'hidden' }}>
          {visible.length === 0 ? (
            <p style={{ padding: 20, color: '#94A3B8', textAlign: 'center' }}>No restaurants.</p>
          ) : visible.map((r, i) => {
            const dirty = drafts[r._id] !== undefined && String(drafts[r._id]) !== String(r.homePriority || 0);
            return (
              <div key={r._id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderTop: i ? '1px solid #F1F5F9' : 'none', background: (r.homePriority || 0) > 0 ? '#F5F8FF' : '#fff' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</div>
                  <div style={{ fontSize: 12, color: '#94A3B8' }}>
                    {r.address?.city || '—'}
                    {r.isVisible === false ? <span style={chip('#FEF2F2', '#B91C1C')}> hidden</span> : null}
                    {r.isApproved === false ? <span style={chip('#FFFBEB', '#B45309')}> not approved</span> : null}
                  </div>
                </div>
                {(r.homePriority || 0) > 0 ? <span style={chip('#EEF2FF', '#4338CA')}>★ {r.homePriority}</span> : null}
                <input
                  style={{ ...inp, width: 90, textAlign: 'center' }}
                  type="number" min="0"
                  value={draftFor(r)}
                  onChange={(e) => setDrafts(prev => ({ ...prev, [r._id]: e.target.value }))}
                />
                <button
                  onClick={() => save(r)}
                  disabled={savingId === r._id || !dirty}
                  style={btn(savedId === r._id ? '#16A34A' : (dirty ? '#2563EB' : '#CBD5E1'), true)}
                >
                  {savingId === r._id ? 'Saving…' : (savedId === r._id ? 'Saved ✓' : 'Save')}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const btn = (bg, small) => ({ background: bg, color: '#fff', border: 'none', borderRadius: 8, padding: small ? '8px 12px' : '10px 16px', fontSize: small ? 13 : 14, fontWeight: 600, cursor: 'pointer' });
const chip = (bg = '#F1F5F9', color = '#334155') => ({ background: bg, color, fontSize: 12, fontWeight: 600, padding: '2px 7px', borderRadius: 6, marginLeft: 6 });
const inp = { padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };

export default HomePriorityManagement;
